import ChartColumn20 from '@carbon/icons-react/es/chart--column/20';
import ChartLineSmooth20 from '@carbon/icons-react/es/chart--line-smooth/20';
import { FormInstance } from 'antd';
import { omit } from 'lodash';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  AggregateType,
  ChartFilter,
  FieldClass,
  FieldOption,
  NativeAndDefaultField,
  WidgetType,
  ZoomLevel,
} from 'api/dashboard/widget';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { PageLoading } from 'components/atoms/loading';
import { Radio } from 'components/atoms/radio';
import { Select } from 'components/atoms/select';
import { Switcher } from 'components/atoms/switcher';
import { GroupedFilterDrawerWithTrigger } from 'components/molecules/filter-drawer';
import { GroupSelect } from 'components/molecules/group-select';
import {
  FilterableField,
  FieldType,
  GroupedOptions,
} from 'components/organisms/tasks/fields';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { ApiFilterTransformer } from 'components/pages/api-filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useTranslate } from 'hooks/use-translation';
import { usePreviewWidgetQuery } from 'queries/widgets';
import {
  getFieldInfo,
  getProjectAndTaskFields,
  transformOptions,
  useChartOptionsWithSortAndDrillDown,
  SortTypes,
} from './chart-formatting-utils';
import { getChartContent } from './chart-widget';
import { ChartWidgetContent } from './chart-widget-content';
import { TOGGLE_CHART_LABEL_LOCALSTORAGE_KEY } from './common';
import {
  CenterWrapper,
  FilterWrapper,
  OptionsCol,
  Row,
  Strong,
  StyledCol,
  StyledFormItem,
} from './styles';
import { ChartType, ObjectType } from './types';
import {
  aggregateRules,
  dateFilterOperationOptions,
  isNativeDateField,
  setAggType,
  transformField,
  useProjectFieldOptions,
  useTaskFieldsOptions,
} from './utils';

const SimpleWrapper = styled.div`
  margin-bottom: var(--spacing-12);
`;
const ChartWidgetWrapper = styled.div`
  width: 40rem;
`;
const LabelOptionWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const LabelWrapper = styled.div`
  margin-left: var(--spacing-04);
`;

const ZoomSwitcher = styled(Switcher)`
  .ant-radio-button-wrapper-checked {
    z-index: 0;
  }
`;
const formatOptionLabel = ({ label, icon }) => {
  return (
    <LabelOptionWrapper>
      {icon}
      <LabelWrapper>{label}</LabelWrapper>
    </LabelOptionWrapper>
  );
};

export type CustomChartFormValues = {
  widgetName: string;
  unitName?: string;
  objectType: string;
  chartType: {
    label: string;
    value: string;
  };
  aggType: AggregateType;
  xAxis: FieldOption;
  yAxis: FieldOption;
  groupBy?: FieldOption;
  zoomLevel?: ZoomLevel;
};

interface CustomChartProps {
  form: FormInstance<CustomChartFormValues>;
  filterFields: ChartFilter;
  groupedFields: GroupedOptions<FilterableField>[];
  defaultGroupedFields: GroupedOptions<FilterableField>[];
  defaultGroup: FilterGroupType;
  transformer: ApiFilterTransformer<GroupedFilter>;
  setFilter: React.Dispatch<React.SetStateAction<ChartFilter>>;
  initialValues: CustomChartFormValues;
  onFinish: (values: CustomChartFormValues) => void;
  isEdit: boolean;
}

const chartLabels = [
  {
    label: 'Column',
    value: ChartType.Column,
    icon: <ChartColumn20 />,
  },
  {
    label: 'Line',
    value: ChartType.Spline,
    icon: <ChartLineSmooth20 />,
  },
];

const zoomLevelOptions = [
  {
    label: 'Weekly',
    value: ZoomLevel.Weekly,
  },
  {
    label: 'Monthly',
    value: ZoomLevel.Monthly,
  },
  {
    label: 'Yearly',
    value: ZoomLevel.Yearly,
  },
];

interface Preferences {
  xAxis: FieldOption;
  yAxis: FieldOption;
  groupBy?: FieldOption;
  aggType: AggregateType;
  zoomLevel?: ZoomLevel;
}

interface ChartFields {
  widgetName: string;
  chartType: {
    label: string;
    value: string;
  };
  objectType: ObjectType;
  projectPreferences: Preferences;
  taskPreferences: Preferences;
  disableTotal: boolean;
  disableAvg: boolean;
}

const projectDefaults = {
  aggType: AggregateType.Total,
  zoomLevel: ZoomLevel.Monthly,
  xAxis: {
    label: 'Project Start Date',
    value: {
      name: 'startDate',
      fieldClass: FieldClass.NativeField,
    },
  },
  yAxis: {
    label: 'Project Count',
    value: {
      name: 'count',
      fieldClass: FieldClass.NativeField,
    },
  },
};

const taskDefaults = {
  aggType: AggregateType.Total,
  zoomLevel: ZoomLevel.Monthly,
  xAxis: {
    label: 'Task Start Date',
    value: {
      name: 'startDate',
      fieldClass: FieldClass.NativeField,
    },
  },
  yAxis: {
    label: 'Task Count',
    value: {
      name: 'count',
      fieldClass: FieldClass.NativeField,
    },
  },
};

export const CustomChart: FC<CustomChartProps> = ({
  form,
  setFilter,
  defaultGroup,
  defaultGroupedFields,
  groupedFields,
  transformer,
  filterFields,
  initialValues,
  onFinish,
  isEdit,
}) => {
  const [chartFields, setCardFields] = useState<ChartFields>(() => {
    let rules;
    let options: {
      projectPreferences: Preferences;
      taskPreferences: Preferences;
    };
    const { widgetName, chartType, objectType, ...preferences } = initialValues;

    if ('name' in initialValues.yAxis.value) {
      rules = aggregateRules(initialValues.yAxis.value.name);
    }

    // both the project and task customisations should be maintained in state, defaults are fallbacks
    switch (objectType) {
      case ObjectType.Project:
        options = {
          projectPreferences: preferences,
          taskPreferences: taskDefaults,
        };
        break;
      case ObjectType.Task:
        options = {
          projectPreferences: projectDefaults,
          taskPreferences: preferences,
        };
        break;
      default:
        options = {
          projectPreferences: projectDefaults,
          taskPreferences: taskDefaults,
        };
        break;
    }

    return {
      widgetName,
      chartType,
      objectType,
      ...options,
      ...rules,
    };
  });

  const translate = useTranslate();
  const {
    groupedProjectNumberFieldOptions,
    groupedProjectCategoricalFieldOptions,
    projectGroupByFieldOptions,
    isfieldsLoading,
  } = useProjectFieldOptions(WidgetType.CustomChart);
  const {
    groupedTaskNumberFieldOptions,
    groupedTaskCategoricalFieldOptions,
    taskGroupByFieldOptions,
    allFields,
    isTaskFieldsLoading,
  } = useTaskFieldsOptions();
  const [showLabels] = useLocalStorage(
    TOGGLE_CHART_LABEL_LOCALSTORAGE_KEY,
    false,
  );

  const [isProjectOrTask, chartPreferences, chartFilter] = useMemo((): [
    boolean,
    Preferences,
    GroupedFilter,
  ] => {
    const _isProjectOrTask = chartFields.objectType === ObjectType.Project;
    const preferences = _isProjectOrTask
      ? chartFields.projectPreferences
      : chartFields.taskPreferences;

    const _chartFilter = _isProjectOrTask
      ? filterFields.projectsFilter
      : filterFields.tasksFilter;

    return [_isProjectOrTask, preferences, _chartFilter];
  }, [
    chartFields.objectType,
    chartFields.projectPreferences,
    chartFields.taskPreferences,
    filterFields.projectsFilter,
    filterFields.tasksFilter,
  ]);

  const nativeDateField = isNativeDateField(chartPreferences?.xAxis?.value);

  const { projectFields, taskFields } = getProjectAndTaskFields(allFields);
  const field = getFieldInfo(
    chartPreferences.xAxis.value,
    chartFields.objectType,
    projectFields,
    taskFields,
  );

  const isDateField = nativeDateField || field?.fieldDataType === 'DATE';

  const chartPayload = {
    simpleSearchFilter: transformer.to(chartFilter),
    widgetType: WidgetType.CustomChart,
    widgetObjectType: chartFields.objectType,
    widgetName: chartFields.widgetName,
    metaData: {
      xAxis: transformField(chartPreferences.xAxis),
      yAxis: transformField(chartPreferences.yAxis),
      ...(chartPreferences?.groupBy && {
        groupBy: chartPreferences?.groupBy?.value,
      }),
      ...(isDateField && {
        zoomLevel: chartPreferences?.zoomLevel,
      }),
      aggType: setAggType(chartPreferences.yAxis, chartPreferences.aggType),
      chart: {
        chartType: chartFields.chartType.value.toUpperCase(),
      },
    },
  };
  const { widgetName, ...variables } = chartPayload;
  const { data: chartOptions, status } = usePreviewWidgetQuery(variables);

  const transformedOptions = transformOptions(
    chartOptions,
    WidgetType.CustomChart,
    chartPayload.metaData,
    field,
    chartFields.objectType,
    showLabels,
  );
  const [defaultChartOptions, , sortType, setSortType] =
    useChartOptionsWithSortAndDrillDown(
      transformedOptions,
      WidgetType.CustomChart,
      isDateField,
    );

  const PreviewChart = getChartContent(status, defaultChartOptions, {
    objectType: chartFields.objectType,
    zoomLevel: chartPreferences.zoomLevel,
    aggType: chartPreferences.aggType,
    filter: transformer.to(chartFilter),
    sortType,
  });

  const filterOnSubmit = (filters) => {
    setFilter((prevFilter): ChartFilter => {
      if (chartFields.objectType === ObjectType.Project) {
        return {
          ...prevFilter,
          projectsFilter: filters,
        };
      }

      return {
        ...prevFilter,
        tasksFilter: filters,
      };
    });
  };

  const switchState = (options, key) => {
    setCardFields((fields) => {
      return isProjectOrTask
        ? {
            ...fields,
            projectPreferences: {
              ...fields.projectPreferences,
              [key]: options,
            },
          }
        : {
            ...fields,
            taskPreferences: {
              ...fields.taskPreferences,
              [key]: options,
            },
          };
    });
  };

  return (
    <>
      {isTaskFieldsLoading || isfieldsLoading ? (
        <PageLoading />
      ) : (
        <Row>
          <StyledCol>
            <CenterWrapper>
              <ChartWidgetWrapper>
                <ChartWidgetContent
                  preview
                  filter={
                    isProjectOrTask
                      ? filterFields.projectsFilter
                      : filterFields.tasksFilter
                  }
                  sortType={sortType}
                  setSortType={setSortType}
                  widgetName={chartFields.widgetName}
                  chartContent={PreviewChart}
                  isDateField={isDateField}
                  widgetType={WidgetType.CustomChart}
                />
              </ChartWidgetWrapper>
            </CenterWrapper>
          </StyledCol>
          <OptionsCol>
            <Form
              layout="vertical"
              form={form}
              initialValues={initialValues}
              onChange={() => setSortType(SortTypes.SortDefault)}
              onFinish={(values) => {
                const customChartFields =
                  nativeDateField || field?.fieldDataType === 'DATE'
                    ? values
                    : omit(values as CustomChartFormValues, 'zoomLevel');

                onFinish(customChartFields as CustomChartFormValues);
              }}
            >
              <Form.Item
                label={translate('dashboard.custom_chart.chart_name')}
                name="widgetName"
                rules={[
                  {
                    whitespace: true,
                    required: true,
                    message: translate(
                      'dashbaord.custom_chart.name.input.error.message',
                    ),
                  },
                ]}
              >
                <Input
                  onChange={(e) => {
                    const { value } = e.target;

                    setCardFields((fields) => {
                      return {
                        ...fields,
                        widgetName: value,
                      };
                    });
                  }}
                  placeholder={translate('dashboard.custom_card.card_name')}
                />
              </Form.Item>
              <Form.Item label="Chart style" name="chartType">
                <Select
                  formatOptionLabel={formatOptionLabel}
                  isSearchable={false}
                  options={chartLabels}
                  onChange={(e) => {
                    setCardFields((fields) => {
                      return {
                        ...fields,
                        chartType: { label: e.label, value: e.value },
                      };
                    });
                  }}
                />
              </Form.Item>

              <Form.Item
                name="objectType"
                label={translate('dashboard.custom_chart.create.chart.base')}
              >
                <Radio.Group
                  disabled={isEdit}
                  value={chartFields.objectType}
                  onChange={(e) => {
                    const formFields = form.getFieldsValue();
                    const preferences =
                      e.target.value === ObjectType.Project
                        ? chartFields.projectPreferences
                        : chartFields.taskPreferences;
                    const rule = aggregateRules(
                      (preferences.yAxis.value as NativeAndDefaultField).name,
                    );

                    form.setFieldsValue({
                      ...formFields,
                      ...preferences,
                      ...(preferences.groupBy
                        ? { groupBy: preferences.groupBy }
                        : { groupBy: null }),
                    } as CustomChartFormValues);

                    setCardFields((fields) => {
                      return {
                        ...fields,
                        ...rule,
                        objectType: e.target.value,
                      };
                    });
                  }}
                >
                  <Radio value={ObjectType.Project}>
                    {translate(
                      'dashboard.custom_chart.create.chart.base.projects',
                    )}
                  </Radio>
                  <Radio value={ObjectType.Task}>
                    {translate(
                      'dashboard.custom_chart.create.chart.base.tasks',
                    )}
                  </Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item label="X-axis" name="xAxis">
                <GroupSelect
                  options={
                    isProjectOrTask
                      ? groupedProjectCategoricalFieldOptions
                      : groupedTaskCategoricalFieldOptions
                  }
                  onChange={(e) => {
                    switchState(e, 'xAxis');
                  }}
                />
              </Form.Item>
              {(nativeDateField || field?.fieldDataType === 'DATE') && (
                <Form.Item label="Scale" name="zoomLevel">
                  <ZoomSwitcher
                    onChange={(e) => switchState(e.target.value, 'zoomLevel')}
                    activeColor="black"
                    value={chartPreferences.zoomLevel}
                  >
                    {zoomLevelOptions.map((zoomLevel) => {
                      return (
                        <Switcher.Button
                          key={zoomLevel.value}
                          value={zoomLevel.value}
                        >
                          {zoomLevel.label}
                        </Switcher.Button>
                      );
                    })}
                  </ZoomSwitcher>
                </Form.Item>
              )}

              <StyledFormItem label="Y-axis" name="yAxis">
                <GroupSelect
                  options={
                    isProjectOrTask
                      ? groupedProjectNumberFieldOptions
                      : groupedTaskNumberFieldOptions
                  }
                  onChange={(e) => {
                    const rule = aggregateRules(e.value.name);

                    if (rule && rule.aggType) {
                      form.setFieldsValue({
                        aggType: rule.aggType,
                      });
                    }
                    switchState(e, 'yAxis');
                    if (rule.aggType) {
                      switchState(rule.aggType, 'aggType');
                    }
                    setCardFields((fields) => {
                      return {
                        ...fields,
                        disableAvg: rule.disableAvg,
                        disableTotal: rule.disableTotal,
                      };
                    });
                  }}
                />
              </StyledFormItem>
              <Form.Item name="aggType">
                <Radio.Group
                  value={
                    isProjectOrTask
                      ? chartFields.projectPreferences.aggType
                      : chartFields.taskPreferences.aggType
                  }
                  onChange={(e) => {
                    switchState(e.target.value, 'aggType');
                  }}
                >
                  <Radio
                    value={AggregateType.Total}
                    disabled={chartFields.disableTotal}
                  >
                    <FormattedMessage id="dashboard.custom_charts.label.total" />
                  </Radio>
                  <Radio
                    value={AggregateType.Average}
                    disabled={chartFields.disableAvg}
                  >
                    <FormattedMessage id="dashboard.custom_charts.label.average" />
                  </Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item label="Group by" name="groupBy">
                <GroupSelect
                  options={
                    isProjectOrTask
                      ? projectGroupByFieldOptions
                      : taskGroupByFieldOptions
                  }
                  value={chartPreferences.groupBy}
                  isClearable
                  onChange={(e) => {
                    switchState(e, 'groupBy');
                  }}
                />
              </Form.Item>

              <Form.Item>
                <FilterWrapper>
                  <Strong>
                    <FormattedMessage id="dashboard.custom_charts.filter" />
                  </Strong>
                </FilterWrapper>
                <SimpleWrapper>
                  <GroupedFilterDrawerWithTrigger
                    defaultGroup={defaultGroup}
                    fields={
                      isProjectOrTask ? defaultGroupedFields : groupedFields
                    }
                    filter={
                      isProjectOrTask
                        ? filterFields.projectsFilter
                        : filterFields.tasksFilter
                    }
                    onSubmit={filterOnSubmit}
                    getFilterOperationTypeOptions={{
                      [FieldType.Date]: dateFilterOperationOptions,
                    }}
                  />
                </SimpleWrapper>
              </Form.Item>
            </Form>
          </OptionsCol>
        </Row>
      )}
    </>
  );
};
